import dayjs, { toUTCDateTimeFormat } from '~/utils/dayjs';
import { getClientTz, shortDate } from './date';
import {
    faArrowUpRightFromSquare,
    faBuilding,
    faContactBook,
    faCrown,
    faDiagramProject,
    faFolderGear,
    faGrid2Plus,
    faListUl,
    faMessages,
    faRobot,
    faRocket,
    faUser,
    type IconDefinition,
} from '@fortawesome/pro-solid-svg-icons';
import {
    faArrowUpRightFromSquare as faArrowUpRightFromSquareReg,
    faBuilding as faBuildingReg,
    faContactBook as faContactBookReg,
    faDiagramProject as faDiagramProjectReg,
    faFolderGear as faFolderGearReg,
    faGrid2Plus as faGrid2PlusReg,
    faListUl as faListUlReg,
    faMessages as faMessagesReg,
    faRobot as faRobotReg,
    faRocket as faRocketReg,
    faUser as faUserReg,
} from '@fortawesome/pro-regular-svg-icons';
import {
    SENDER_TYPE_CORPORATE,
    SENDER_TYPE_GROUP,
    SENDER_TYPE_ORG_UNIT,
    type SenderType,
} from '~/constants/senderTypes';
import { FEATURES } from '~/provides/features';
import type { FeatureID } from '~/provides/features.types';
import type { Dayjs } from 'dayjs';
import type { OutboundMessagesParams } from '~/services/analytics/outboundMessages/outboundMessagesAnalyticsClient.types';
import type { AnalyticsDateRange } from '~/services/analytics/analyticsClient.types';
import type {
    WorkflowVersionParticipationAnchor,
    WorkflowVersionParticipationParams,
} from '~/services/workflows/workflowAnalyticsClient.types';
import type { AnalyticsCategory } from '~/constants/categories';

const allResourceTypesOptions = [
    { label: 'Message Hub', value: 'user' },
    { label: 'Blast Campaign', value: 'one_time_campaign' },
    { label: 'Evergreen Campaign', value: 'evergreen_campaign' },
    { label: 'External Campaign', value: 'external_campaign' },
    { label: 'Automation', value: 'automation' },
    { label: 'Workflow', value: 'workflow' },
    { label: 'Other', value: 'other' },
] as const;

// Gated resource types are only offered to teams with the feature;
// the label and icon lookups keep every entry so existing data still renders.
const resourceTypeFeatures: Partial<Record<ResourceType, FeatureID>> = {
    external_campaign: 'external_campaigns',
    workflow: 'workflow_builder',
};

export const resourceTypesOptions = allResourceTypesOptions.filter((option) => {
    const feature = resourceTypeFeatures[option.value];

    return feature === undefined || FEATURES[feature];
});
export const resourceTypeValues = resourceTypesOptions.map((option) => option.value);

export const resourceTypeLabel = (resourceType: ResourceType) => {
    const resource = allResourceTypesOptions.find((option) => option.value === resourceType);

    return resource ? resource.label : resourceType;
};

export const resourceTypeIcon = (resourceType: ResourceType, weight: 'solid' | 'regular' = 'solid') => {
    const defaultIcon = weight === 'regular' ? faFolderGearReg : faFolderGear;
    let icons: Record<ResourceType, IconDefinition>;

    if (weight === 'regular') {
        icons = {
            user: faUserReg,
            one_time_campaign: faRocketReg,
            evergreen_campaign: faMessagesReg,
            external_campaign: faArrowUpRightFromSquareReg,
            automation: faRobotReg,
            workflow: faDiagramProjectReg,
            other: defaultIcon,
        };
    } else {
        icons = {
            user: faUser,
            one_time_campaign: faRocket,
            evergreen_campaign: faMessages,
            external_campaign: faArrowUpRightFromSquare,
            automation: faRobot,
            workflow: faDiagramProject,
            other: defaultIcon,
        };
    }

    return icons[resourceType];
};

export type ResourceType = (typeof allResourceTypesOptions)[number]['value'];

export const isCampaign = (resource: { resource_type: ResourceType } | ResourceType) => {
    const possibilities = ['one_time_campaign', 'evergreen_campaign', 'campaign'];

    if (typeof resource === 'string') {
        return possibilities.includes(resource);
    }

    return possibilities.includes(resource.resource_type);
};

export const metricsOptions = [
    {
        abbreviation: 'Messages',
        label: 'Messages Sent',
        value: 'total_messages_sent',
        description: 'The total number of messages sent by Voxie during the selected period.',
        uses_percentage: false,
    },
    {
        abbreviation: 'Unique CTR',
        label: 'Unique Click-Through Rate',
        value: 'unique_click_through_rate',
        description: 'Measures the percentage of individual recipients who clicked in messages sent containing a link.',
        uses_percentage: true,
    },
    {
        abbreviation: 'Click-Through',
        label: 'Click-Through Rate',
        value: 'click_through_rate',
        description: 'Measures total clicks divided by total messages sent containing a link.',
        uses_percentage: true,
    },
    {
        abbreviation: 'Unsubscribe',
        label: 'Unsubscribe Rate',
        value: 'opt_out_rate',
        description: 'The percentage of recipients who opted out after receiving a message.',
        uses_percentage: true,
    },
    {
        abbreviation: 'Reply',
        label: 'Reply Rate',
        value: 'message_reply_rate',
        description: 'The percentage of messages that received a customer response.',
        uses_percentage: true,
    },
    {
        abbreviation: 'Cost',
        label: 'Messaging Cost',
        value: 'messaging_cost',
        description: 'The total cost of messages sent.',
        uses_percentage: false,
        uses_currency: true,
    },
] as const;

export type Metric = (typeof metricsOptions)[number]['value'];

export const metricsWithGoals = [
    'unique_click_through_rate',
    'click_through_rate',
    'opt_out_rate',
    'message_reply_rate',
];

export const reversedMetrics = ['opt_out_rate'];
export const isReversedMetric = (metric: Metric) => reversedMetrics.includes(metric);

export const getMetricByValue = (metricValue: Metric) => metricsOptions.find((option) => option.value === metricValue);

export const metricAbbreviation = (metricValue: Metric) => {
    const metric = metricsOptions.find((option) => option.value === metricValue);

    return metric ? metric.abbreviation : metricValue;
};

export const metricLabel = (metricValue: Metric) => {
    const metric = metricsOptions.find((option) => option.value === metricValue);

    return metric ? metric.label : metricValue;
};

export const audienceOriginTypes = [
    { label: 'Automation', value: 'automation' },
    { label: 'Segment', value: 'segment' },
    { label: 'External Contact List', value: 'external_contact_list' },
    { label: 'Workflow', value: 'workflow' },
    { label: 'Other', value: 'other' },
] as const;

export type AudienceOriginType = (typeof audienceOriginTypes)[number]['value'];

export const audienceOriginTypeIcon = (
    audienceOriginType: AudienceOriginType,
    weight: 'solid' | 'regular' = 'solid'
) => {
    const defaultIcon = weight === 'regular' ? faFolderGearReg : faFolderGear;
    let icons: Record<AudienceOriginType, IconDefinition>;

    if (weight === 'regular') {
        icons = {
            segment: faContactBookReg,
            external_contact_list: faListUlReg,
            automation: faRobotReg,
            workflow: faDiagramProjectReg,
            other: defaultIcon,
        };
    } else {
        icons = {
            segment: faContactBook,
            external_contact_list: faListUl,
            automation: faRobot,
            workflow: faDiagramProject,
            other: defaultIcon,
        };
    }

    return icons[audienceOriginType];
};

export const audienceOriginTypeLabel = (audienceOriginType: AudienceOriginType): string => {
    return audienceOriginTypes.find((option) => option.value === audienceOriginType)?.label || audienceOriginType;
};

export const generateLegendColors = (list: string[]) => {
    const fixedColors = [
        {
            iconColor: '#3B82F6', // blue
            bgColor: '#93C5FD',
            labels: ['Marketing', 'marketing'],
        },
        {
            iconColor: '#D946EF', // fuchsia
            bgColor: '#F0ABFC',
            labels: ['Transactional', 'transactional'],
        },
        {
            iconColor: '#F43F5E', // rose
            bgColor: '#FDA4AF',
            labels: ['Acquisition', 'acquisition'],
        },
        {
            iconColor: '#F59E0B', // amber
            bgColor: '#FCD34D',
            labels: ['Re-acquisition', 're_acquisition'],
        },
        {
            iconColor: '#14B8A6', // teal
            bgColor: '#5EEAD4',
            labels: ['Compliance', 'compliance'],
        },
    ];

    const availableColors = [
        {
            // blue
            iconColor: '#3B82F6',
            bgColor: '#93C5FD',
        },
        {
            // fuchsia
            iconColor: '#D946EF',
            bgColor: '#F0ABFC',
        },
        {
            // rose
            iconColor: '#F43F5E',
            bgColor: '#FDA4AF',
        },
        {
            // amber
            iconColor: '#F59E0B',
            bgColor: '#FCD34D',
        },
        {
            // lime
            iconColor: '#84CC16',
            bgColor: '#BEF264',
        },
        {
            // violet
            iconColor: '#8B5CF6',
            bgColor: '#C4B5FD',
        },
        {
            // sky
            iconColor: '#14ABE0',
            bgColor: '#73D8FC',
        },

        {
            // pink
            iconColor: '#EC4899',
            bgColor: '#F9A8D4',
        },
        {
            // green
            iconColor: '#22c55e',
            bgColor: '#86efac',
        },

        {
            // orange
            iconColor: '#F97316',
            bgColor: '#FDBA74',
        },

        {
            // blue gray
            iconColor: '#64748B',
            bgColor: '#CBD5E1',
        },
        {
            // cyan
            iconColor: '#06b6d4',
            bgColor: '#67e8f9',
        },
        {
            // slate
            iconColor: '#64748b',
            bgColor: '#cbd5e1',
        },
    ];

    const usedColors = new Set();

    return list.map((item) => {
        const fixedColor = fixedColors.find((color) => color.labels.includes(item) && !usedColors.has(color.iconColor));

        if (fixedColor) {
            usedColors.add(fixedColor.iconColor);
            return { label: item, iconColor: fixedColor.iconColor, bgColor: fixedColor.bgColor };
        }

        const color = availableColors.find((color) => !usedColors.has(color.iconColor));

        if (color) {
            usedColors.add(color.iconColor);
            return { label: item, iconColor: color.iconColor, bgColor: color.bgColor };
        }

        return {
            label: item,
            iconColor: '#000',
            bgColor: '#ccc',
        };
    });
};

export const BILLING_CUTOFF_DATE = '2025-01-01T05:00:00Z';

export const minUniqueClickThroughRateDate = dayjs.utc(BILLING_CUTOFF_DATE);
export const minUniqueClickThroughRateComparisonDate = dayjs.utc('2024-12-01 05:00:00');

const minAllowedDate = dayjs.utc('2024-07-01 04:00:00');
const minAllowedComparisonDate = dayjs.utc('2024-06-01 04:00:00');

const minOrgUnitAllowedDate = dayjs.utc('2025-08-01 05:00:00');
const minOrgUnitAllowedComparisonDate = dayjs.utc('2025-07-01 05:00:00');

const minGroupAllowedDate = dayjs.utc('2025-02-01 05:00:00');
const minGroupAllowedComparisonDate = dayjs.utc('2025-01-01 05:00:00');

export const getMinAllowedDate = (groupMinDate: boolean = false, orgUnitMinDate: boolean = false): Dayjs => {
    if (orgUnitMinDate) {
        return minOrgUnitAllowedDate;
    }

    if (groupMinDate) {
        return minGroupAllowedDate;
    }

    return minAllowedDate;
};

export const getMinAllowedComparisonDate = (groupMinDate: boolean = false, orgUnitMinDate: boolean = false): Dayjs => {
    if (orgUnitMinDate) {
        return minOrgUnitAllowedComparisonDate;
    }

    if (groupMinDate) {
        return minGroupAllowedComparisonDate;
    }

    return minAllowedComparisonDate;
};

export const calculateDifference = (numbers: [number, number, ...number[]]): number | null => {
    return numbers[0] !== 0 ? (numbers[1] - numbers[0]) / numbers[0] : null;
};

export const getCurrentDate = (): Dayjs => {
    return dayjs().tz(getClientTz()).subtract(30, 'minutes');
};

// dayjs calculates an end date starting from a next day. in order to get the right range including a current day we decrease max days by 1.
export const MAX_CUSTOM_RANGE_DAYS = 99;

export const getMaxAllowedDate = (): Dayjs => {
    const currentDate = getCurrentDate();

    // current day is only available if the time is at least 01:30 local.
    if (currentDate.isAfter(currentDate.clone().set('hour', 1).set('minute', 30))) {
        return currentDate;
    }

    return currentDate.subtract(1, 'day').endOf('day');
};

export const formatDateForRange = (date: Dayjs): string => {
    return toUTCDateTimeFormat(date.startOf('hour'));
};

type DateRange = { start_date: string | null; end_date: string | null };

export const getDateRange = (
    value: string,
    groupMinDate: boolean = false,
    orgUnitMinDate: boolean = false
): DateRange => {
    const currentDate = getCurrentDate();

    if (value === 'last_7_days') {
        const startDate =
            currentDate.hour() === 0
                ? currentDate.clone().startOf('day').subtract(7, 'days')
                : currentDate.clone().startOf('day').subtract(6, 'days');

        const endDate = currentDate.clone();

        return {
            start_date: formatDateForRange(startDate),
            end_date: formatDateForRange(endDate),
        };
    }

    if (value === 'last_14_days') {
        const startDate =
            currentDate.hour() === 0
                ? currentDate.clone().startOf('day').subtract(14, 'days')
                : currentDate.clone().startOf('day').subtract(13, 'days');

        const endDate = currentDate.clone();

        return {
            start_date: formatDateForRange(startDate),
            end_date: formatDateForRange(endDate),
        };
    }

    if (value === 'monday_today') {
        const startDate = currentDate.clone().startOf('isoWeek');

        const endDate = currentDate.clone();

        return {
            start_date: formatDateForRange(startDate),
            end_date: formatDateForRange(endDate),
        };
    }

    if (value === 'month_today') {
        const startDate = currentDate.clone().startOf('month');

        const endDate = currentDate.clone();

        return {
            start_date: formatDateForRange(startDate),
            end_date: formatDateForRange(endDate),
        };
    }

    if (value === 'previous_month') {
        const startDate = currentDate.clone().subtract(1, 'month').startOf('month');

        const endDate = currentDate.clone().startOf('month');

        return {
            start_date: formatDateForRange(startDate),
            end_date: formatDateForRange(endDate),
        };
    }

    if (value === 'last_30_days') {
        const startDate =
            currentDate.hour() === 0
                ? currentDate.clone().startOf('day').subtract(30, 'days')
                : currentDate.clone().startOf('day').subtract(29, 'days');

        const endDate = currentDate.clone();

        return {
            start_date: formatDateForRange(startDate),
            end_date: formatDateForRange(endDate),
        };
    }

    if (value === 'last_90_days') {
        const startDate =
            currentDate.hour() === 0
                ? currentDate.clone().startOf('day').subtract(90, 'days')
                : currentDate.clone().startOf('day').subtract(89, 'days');

        if (startDate.isBefore(getMinAllowedComparisonDate(groupMinDate, orgUnitMinDate))) {
            return {
                start_date: null,
                end_date: null,
            };
        }
        const endDate = currentDate.clone();

        return {
            start_date: formatDateForRange(startDate),
            end_date: formatDateForRange(endDate),
        };
    }

    return {
        start_date: null,
        end_date: null,
    };
};

type DateRangeOption = {
    value: string;
    label: string;
    start_date?: string | null;
    end_date?: string | null;
};

export const SELECTED_CUSTOM_RANGE = 'selected_custom';

export const findDateRangeOption = (
    [startDate, endDate]: Date[],
    groupMinDate: boolean = false,
    orgUnitMinDate: boolean = false
): DateRangeOption => {
    const option = controlDateRangeOptions(groupMinDate, orgUnitMinDate).find(
        (option) => dayjs(startDate).isSame(option.start_date) && dayjs(endDate).isSame(option.end_date)
    );

    if (option) {
        return option;
    }

    return {
        value: SELECTED_CUSTOM_RANGE,
        start_date: formatDateForRange(dayjs(startDate)),
        end_date: formatDateForRange(dayjs.min([getCurrentDate(), dayjs(endDate).add(1, 'day')])),
        label: `${shortDate(startDate)} - ${shortDate(endDate)}`,
    };
};

/**
 * This needs to be a function in order to use dayjs in export.
 */
export const controlDateRangeOptions = (
    groupMinDate: boolean = false,
    orgUnitMinDate: boolean = false
): DateRangeOption[] => {
    const options = [
        {
            value: 'last_7_days',
            label: 'Last 7 Days',
            ...getDateRange('last_7_days'),
        },
        {
            value: 'monday_today',
            label: 'Monday To Today',
            ...getDateRange('monday_today'),
        },
        {
            value: 'month_today',
            label: 'Current Month',
            ...getDateRange('month_today'),
        },
        {
            value: 'previous_month',
            label: 'Last Month',
            ...getDateRange('previous_month'),
        },
        {
            value: 'last_30_days',
            label: 'Last 30 Days',
            ...getDateRange('last_30_days'),
        },
        {
            value: 'last_90_days',
            label: 'Last 90 Days',
            ...getDateRange('last_90_days', groupMinDate, orgUnitMinDate),
        },
        {
            value: 'custom',
            label: 'Custom Period',
        },
    ];

    return options.filter(
        (option: DateRangeOption) =>
            option.value !== 'last_90_days' || (option.value === 'last_90_days' && option.start_date && option.end_date)
    );
};

export const computeComparisonRange = (
    range: { start: string; end: string } | string[],
    groupMinDate: boolean = false,
    orgUnitMinDate: boolean = false
): string[] => {
    let start;
    let end;

    if (Array.isArray(range)) {
        start = range[0];
        end = range[1];
    } else {
        start = range.start;
        end = range.end;
    }

    const startDate = dayjs(start).tz(getClientTz());
    const endDate = dayjs(end).tz(getClientTz());

    const daysDiff = Math.ceil(endDate.diff(startDate, 'days', true));

    const comparisonDate = startDate.clone().subtract(daysDiff, 'days');

    if (comparisonDate.isBefore(getMinAllowedComparisonDate(groupMinDate, orgUnitMinDate))) {
        return [toUTCDateTimeFormat(startDate), toUTCDateTimeFormat(endDate)];
    }

    return [toUTCDateTimeFormat(comparisonDate), toUTCDateTimeFormat(startDate), toUTCDateTimeFormat(endDate)];
};

export const calculateRangeBetweenTwoDates = ({
    startDate,
    endDate,
}: {
    startDate: string;
    endDate: string;
}): string[] => {
    const dates = [];
    const startDayjs = dayjs(startDate).tz(getClientTz());
    const endDayjs = dayjs(endDate).tz(getClientTz());

    let currentDate = startDayjs.clone();

    dates.push(toUTCDateTimeFormat(currentDate));

    while (currentDate.unix() < endDayjs.unix()) {
        currentDate = currentDate.add(1, 'day');
        dates.push(toUTCDateTimeFormat(currentDate));
    }

    dates[dates.length - 1] = toUTCDateTimeFormat(dayjs(endDate));

    return dates;
};

export const validateRangeDates = (dates: AnalyticsDateRange | null | undefined): boolean => {
    if (!Array.isArray(dates) || !dates.length || dates.length < 2 || dates.length > 100) {
        return false;
    }

    return dates.every((date) => dayjs(date, 'Y-m-dTH:00:00Z').isValid());
};

export const metricsInputValidator = (fetchParams: OutboundMessagesParams): boolean => {
    if (!fetchParams || fetchParams.constructor !== Object) {
        console.warn('Invalid prop passed in metricsInputValidator', fetchParams);
        return false;
    }

    if (!validateRangeDates(fetchParams.range)) {
        console.warn('Invalid range in metricsInputValidator', fetchParams);
        return false;
    }

    if (
        fetchParams?.filters?.resource_types &&
        (!Array.isArray(fetchParams.filters.resource_types) ||
            fetchParams.filters.resource_types.length === 0 ||
            fetchParams.filters.resource_types.length > allResourceTypesOptions.length)
    ) {
        console.warn('Invalid filters.resource_types in metricsInputValidator', fetchParams);
        return false;
    }

    if (
        fetchParams?.filters?.audience_types &&
        (!Array.isArray(fetchParams.filters.audience_types) ||
            fetchParams.filters.audience_types.length === 0 ||
            fetchParams.filters.audience_types.length > 5)
    ) {
        console.warn('Invalid filters.audience_types in metricsInputValidator', fetchParams);
        return false;
    }

    if (
        fetchParams?.filters?.resources &&
        (!Array.isArray(fetchParams.filters.resources) ||
            fetchParams.filters.resources.length === 0 ||
            fetchParams.filters.resources.length > 10)
    ) {
        console.warn('Invalid filters.resources in metricsInputValidator', fetchParams);
        return false;
    }

    return true;
};

export const lowestAndHighestExceptZeroes = (values?: number[] | null): [number, number] => {
    const filtered = (values || []).filter((value) => value).sort((a, b) => b - a);

    return [filtered[filtered.length - 1] || 0, filtered[0] || 0];
};

export const shouldShowResourceDetails = (resource: { resource_type: ResourceType }): boolean =>
    ['one_time_campaign', 'evergreen_campaign', 'automation'].includes(resource.resource_type);

export function getSenderTypeIcon(senderType: SenderType, weight: 'solid' | 'regular' = 'solid'): IconDefinition {
    if (weight === 'regular') {
        switch (senderType) {
            case SENDER_TYPE_CORPORATE:
                return faCrown;
            case SENDER_TYPE_ORG_UNIT:
                return faBuildingReg;
            case SENDER_TYPE_GROUP:
                return faGrid2PlusReg;
        }
    }

    switch (senderType) {
        case SENDER_TYPE_CORPORATE:
            return faCrown;
        case SENDER_TYPE_ORG_UNIT:
            return faBuilding;
        case SENDER_TYPE_GROUP:
            return faGrid2Plus;
    }
}

export function getSenderTypeColorClass(senderType: SenderType): string {
    switch (senderType) {
        case SENDER_TYPE_CORPORATE:
            return 'text-amber-400';
        case SENDER_TYPE_ORG_UNIT:
            return 'text-purple-500';
        case SENDER_TYPE_GROUP:
            return 'text-slate-500';
    }
}

export const resolveDateRange = (
    rangeValue: string,
    customRange: [string, string] | null = null
): [string, string] | null => {
    if (SELECTED_CUSTOM_RANGE === rangeValue) {
        return customRange;
    }

    const { start_date: startDate, end_date: endDate } = getDateRange(rangeValue);

    return null === startDate || null === endDate ? null : [startDate, endDate];
};

export const buildWorkflowVersionParticipationRequest = (
    workflowId: number,
    version: number,
    rangeValue: string,
    customRange: [string, string] | null,
    anchor: WorkflowVersionParticipationAnchor
): WorkflowVersionParticipationParams | null => {
    const range = resolveDateRange(rangeValue, customRange);

    if (null === range) {
        return null;
    }

    return {
        workflow_id: workflowId,
        version,
        anchor,
        range,
    };
};

export function addCategoryToFetchParams(
    fetchParams: OutboundMessagesParams,
    category: AnalyticsCategory
): OutboundMessagesParams {
    return {
        ...fetchParams,
        filters: {
            ...fetchParams.filters,
            resource_categories: [category],
        },
    };
}
